<template>
  <component
    :is="resolvedTag"
    class="play-link"
    :class="[
      `play-link--${variant}`,
      { 'play-link--active': active, 'play-link--disabled': disabled },
    ]"
    :to="resolvedTag === 'router-link' ? to : null"
    :href="resolvedTag === 'a' ? (disabled ? null : href || '#') : null"
    :target="target || null"
    :rel="computedRel"
    :aria-disabled="disabled ? 'true' : null"
    :tabindex="disabled ? -1 : null"
    v-bind="$attrs"
    v-on="$listeners"
    @click="onClick"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <slot />
  </component>
</template>

<script>
/**
 * PlayLink — text/navigation link for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-link>. Renders <router-link> when `to` is set, else an
 * <a> (or a plain <span> when `disabled`, so it is inert + unfocusable).
 *
 * Security: when `target="_blank"`, `rel="noopener noreferrer"` is added
 * automatically (b-link did this too) unless the caller passes their own `rel`.
 *
 * Looks: `variant` picks the style — `default` (inherits color, animated
 * underline on hover), `muted`, `plain` (no underline), or `mark` (lime
 * highlighter sweep). `current` keeps the surrounding text color.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism; drop it on Vue 3
 * (v-bind="$attrs" keeps forwarding listeners). Props/slots are Vue 3-safe.
 */
export default {
  name: 'PlayLink',
  inheritAttrs: false,
  props: {
    /** router-link target — when set, renders <router-link> */
    to: {
      type: [String, Object],
      default: null,
    },
    /** external href — renders <a> */
    href: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '',
    },
    /** Overrides the auto rel (which is set for target=_blank) */
    rel: {
      type: String,
      default: '',
    },
    /** Visual role */
    variant: {
      type: String,
      default: 'default',
      validator: v => ['default', 'muted', 'plain', 'mark', 'current'].includes(v),
    },
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedTag() {
      if (this.disabled) return 'span'
      if (this.to) return 'router-link'
      return 'a'
    },
    computedRel() {
      if (this.rel) return this.rel
      if (this.target === '_blank') return 'noopener noreferrer'
      return null
    },
  },
  methods: {
    onClick(e) {
      if (this.disabled) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      this.$emit('click', e)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-link {
  --lk-color: var(--c-secondary);
  display: inline;
  color: var(--lk-color);
  font-family: inherit;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  background-image: linear-gradient(var(--lk-color), var(--lk-color));
  background-repeat: no-repeat;
  background-position: 0 100%;
  background-size: 0% 2px;
  transition: background-size var(--dur) var(--ease-out), color var(--dur-fast) var(--ease-out);
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &.play-link--active { background-size: 100% 2px; }

  &:focus-visible {
    outline: 2px solid var(--c-secondary);
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* ── Variants ── */
  &--muted   { --lk-color: var(--text-muted); }
  &--current { --lk-color: currentColor; }

  &--plain {
    background-image: none;
    &:hover { color: var(--c-secondary-strong); }
  }

  &--mark {
    --lk-color: var(--text-strong);
    background-image: linear-gradient(var(--c-highlight), var(--c-highlight));
    background-size: 100% 0;
    background-position: 0 88%;
    transition: background-size var(--dur) var(--ease-bounce);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    &:hover,
    &.play-link--active { background-size: 100% 45%; }
  }

  /* ── Disabled (renders as <span>) ── */
  &--disabled {
    color: var(--text-muted);
    background-image: none;
    cursor: not-allowed;
    pointer-events: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-link { transition: none; }
}
</style>
